/**
 * Weapon catalogue per unit kind.
 *
 * Combat in Galaxy Ranger gates damage on choosing the right weapon for the
 * target — wrong weapon → energy spent silently, no damage. This module
 * encodes the per-unit weapon list and the predicate for "is this weapon
 * appropriate for that target right now."
 *
 * Distance bands are Chebyshev-cell ranges. The decomp doesn't surface exact
 * thresholds for the gun's power tiers; we use the help.txt-implied bands
 * (short / middle / large) and a sensible split of the sight range.
 */

import { type Position, UnitKind, chebyshev } from "./types.js";
import type { Unit } from "./units.js";

export interface WeaponSpec {
  /** Index into the unit's arsenal — what the player picks in the weapon menu. */
  readonly index: number;
  /** Display name. Spoken when navigating the weapon menu. */
  readonly name: string;
  /** Sound id (without the `.wav`). */
  readonly sound: string;
  /** Returns true iff this weapon is the right choice for the given target now. */
  readonly isAppropriate: (target: Unit, distance: number, ctx: TargetContext) => boolean;
  /** What the player should hear when asked "which weapon to use" (Z key). */
  readonly hint: string;
}

export interface TargetContext {
  /** For the rocket plant: enemy ship "height" — added to the distance band. */
  readonly height?: number;
}

const wpn = (
  index: number,
  name: string,
  sound: string,
  hint: string,
  isAppropriate: WeaponSpec["isAppropriate"],
): WeaponSpec => ({ index, name, sound, hint, isAppropriate });

/**
 * Soldier carries three weapons keyed to target class. The `sound` is the
 * weapon's firing/impact sound in the soldier driver folder (level5).
 */
const SOLDIER_WEAPONS: readonly WeaponSpec[] = [
  wpn(0, "laser gun", "laser_gun", "Use the laser gun for enemy soldiers.",
    (t) => t.kind === UnitKind.Soldier),
  wpn(1, "grenade discharger", "grenade_shot", "Use the grenade discharger for enemy electronic guns.",
    (t) => t.kind === UnitKind.ElectronicGun),
  wpn(2, "rocket launcher", "missil_shot", "Use the rocket launcher for enemy rocket plants.",
    (t) => t.kind === UnitKind.RocketPlant),
];

/**
 * Electronic gun chooses power tier by Chebyshev distance to the enemy tank.
 * Firing sound is the same `gun_shot` for every tier — the player's choice
 * affects whether the shot lands, not what it sounds like.
 */
const GUN_WEAPONS: readonly WeaponSpec[] = [
  wpn(0, "low power", "gun_shot", "Use low power at close range.",
    (_t, d) => d <= 2),
  wpn(1, "middle power", "gun_shot", "Use middle power at middle distance.",
    (_t, d) => d === 3),
  wpn(2, "high power", "gun_shot", "Use high power at long distance.",
    (_t, d) => d >= 4),
];

/**
 * Rocket plant chooses missile by distance + ship height. Per the manual:
 *   sum 1 or 2 → missile 1
 *   sum 3 or 4 → missile 2
 *   sum 5 or 6 → missile 3
 * The plant's radar range (distance < 7) intentionally exceeds its fire
 * range (sum ≤ 6) — ships at the edge of detection must close in before
 * they can be hit. This is a tactical feature of the original design,
 * not a defect.
 */
const PLANT_WEAPONS: readonly WeaponSpec[] = [
  wpn(0, "missile 1", "missil_shot", "Use missile 1 when the sum is 1 or 2.",
    (_t, d, ctx) => d + (ctx.height ?? 0) <= 2),
  wpn(1, "missile 2", "missil_shot", "Use missile 2 when the sum is 3 or 4.",
    (_t, d, ctx) => {
      const sum = d + (ctx.height ?? 0);
      return sum === 3 || sum === 4;
    }),
  wpn(2, "missile 3", "missil_shot", "Use missile 3 when the sum is 5 or 6.",
    (_t, d, ctx) => {
      const sum = d + (ctx.height ?? 0);
      return sum === 5 || sum === 6;
    }),
];

/**
 * Tank picks weapon by enemy class (laser→soldier, electronic→tank, photon→scout).
 * The level8 folder ships short firing samples (`laser`, `photon`) alongside the
 * spoken weapon names (`laser_gun`, `weapons`) — we want the firing sound here.
 */
const TANK_WEAPONS: readonly WeaponSpec[] = [
  wpn(0, "laser gun", "laser", "Use the laser gun for enemy soldiers.",
    (t) => t.kind === UnitKind.Soldier),
  wpn(1, "electronic gun", "missil_shot", "Use the electronic gun for enemy tanks.",
    (t) => t.kind === UnitKind.SpaceTank),
  wpn(2, "photon missile", "photon", "Use the photon missile for enemy scouts.",
    (t) => t.kind === UnitKind.SpaceScout),
];

/**
 * Cruiser picks weapon by enemy class. Level10 ships uppercase short firing
 * samples (`LASER`, `PHOTON`) — we reference the lowercase names here and let
 * the sound-map remap to the actual filenames.
 */
const CRUISER_WEAPONS: readonly WeaponSpec[] = [
  wpn(0, "electronic gun", "electronic", "Use the electronic gun for enemy tanks.",
    (t) => t.kind === UnitKind.SpaceTank),
  wpn(1, "photon missile", "photon", "Use the photon missile for enemy scouts.",
    (t) => t.kind === UnitKind.SpaceScout),
  wpn(2, "gravitation torpedo", "gravitation", "Use the gravitation torpedo for enemy cruisers.",
    (t) => t.kind === UnitKind.SpaceCruiser),
];

/** Scout has no weapons — it only spots. */
const NO_WEAPONS: readonly WeaponSpec[] = [];

export const WEAPONS: Readonly<Record<UnitKind, readonly WeaponSpec[]>> = {
  [UnitKind.Worker]: NO_WEAPONS,
  [UnitKind.Truck]: NO_WEAPONS,
  [UnitKind.Soldier]: SOLDIER_WEAPONS,
  [UnitKind.ElectronicGun]: GUN_WEAPONS,
  [UnitKind.RocketPlant]: PLANT_WEAPONS,
  [UnitKind.SpaceTank]: TANK_WEAPONS,
  [UnitKind.SpaceScout]: NO_WEAPONS,
  [UnitKind.SpaceCruiser]: CRUISER_WEAPONS,
};

/**
 * Identify which weapon a unit should pick for a given target.
 * Useful for the Z-key "which weapon to use" hint.
 */
export function recommendWeapon(
  attacker: Unit,
  target: Unit,
  ctx: TargetContext = {},
): WeaponSpec | null {
  const list = WEAPONS[attacker.kind];
  const distance = chebyshev(attacker.position, target.position);
  for (const w of list) {
    if (w.isAppropriate(target, distance, ctx)) return w;
  }
  return null;
}

/** Look up a weapon by its menu index in a unit's arsenal. */
export function weaponAt(kind: UnitKind, index: number): WeaponSpec | null {
  const list = WEAPONS[kind];
  return list[index] ?? null;
}

/** Compute a "ship height" placeholder. The original derives it from a runtime
 * field on ships; for the recreation we use a deterministic small value per kind
 * so the missile-1/2/3 mechanic still functions.
 */
export function shipHeight(targetKind: UnitKind, targetPos: Position): number {
  // Mix the target's grid position into a small 0..2 band so the value isn't
  // constant across the game but is reproducible for a given unit instance.
  if (targetKind === UnitKind.SpaceScout) return ((targetPos.x + targetPos.y) % 3);
  if (targetKind === UnitKind.SpaceCruiser) return ((targetPos.x + targetPos.y) % 3) + 1;
  return 0;
}
